import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import {
  buildKpiDateRange,
  coerceKpiNumber,
  extractDailyRows,
} from "../_kpiDenseDailyUtils";
import { AppStrings } from "../../utils/constants/AppStrings";
import { buildCasinoKpiMoneyTypeParam } from "./casinoKpiFilters";

export type CasinoDailyPerformanceFilters = {
  start_date: string;
  end_date: string;
  "provider[eq]"?: string;
  "game[eq]"?: string;
  money_type?: string[];
};

export type CasinoDailyPoint = {
  date: string;
  turnover: number;
  ggr: number;
  rounds: number;
};

export type CasinoDailyFilterOption = { value: string; label: string };

/**
 * Provider + game catalog the BE bundles into the daily response via
 * `filters`. Drives the two dropdowns above the chart.
 *
 * `providers` is always populated (with the "all" catch-all prepended).
 *
 * `gamesByProvider` is a client-side cascade map keyed by provider
 * name — the BE ships the full map inside `filter.game.cascading_options`
 * so the FE can resolve the game list synchronously on provider change
 * without waiting for a refetch. Each value is a pre-shaped option list
 * with the "All Games" catch-all already prepended so the component
 * doesn't have to. Providers with no games in the current date range
 * are still present in the map (with just the catch-all row).
 */
export type CasinoDailyFilterOptions = {
  providers: CasinoDailyFilterOption[];
  gamesByProvider: Record<string, CasinoDailyFilterOption[]>;
};

export type CasinoDailyResult = {
  points: CasinoDailyPoint[];
  filterOptions: CasinoDailyFilterOptions;
};

const ALL_VALUE = "all";

const ALL_PROVIDERS_OPTION: CasinoDailyFilterOption = {
  value: ALL_VALUE,
  label: AppStrings.ALL_PROVIDERS,
};
const ALL_GAMES_OPTION: CasinoDailyFilterOption = {
  value: ALL_VALUE,
  label: AppStrings.ALL_GAMES,
};

// Builder rather than singleton: each call returns a fresh object so a
// caller mutating `result.providers` (e.g. `.push(...)`) can never corrupt
// the catch-all baseline for the next render or another component.
const buildEmptyFilterOptions = (): CasinoDailyFilterOptions => ({
  providers: [ALL_PROVIDERS_OPTION],
  gamesByProvider: {},
});

type RawDailyRow = {
  date?: string | null;
  turnover?: number | string | null;
  ggr?: number | string | null;
  rounds?: number | string | null;
  total_rounds?: number | string | null;
};

const mapRow = (row: RawDailyRow): CasinoDailyPoint | null => {
  const date = typeof row?.date === "string" ? row.date.trim() : "";
  if (!date) return null;

  return {
    date,
    turnover: coerceKpiNumber(row.turnover),
    ggr: coerceKpiNumber(row.ggr),
    // Backend may emit `rounds` or `total_rounds`; UI cares about `rounds`.
    rounds: coerceKpiNumber(row.rounds ?? row.total_rounds),
  };
};

/** Maps the casino daily HTTP envelope into dense, chart-ready points. */
export const parseCasinoDailyResponse = (
  body: unknown,
  filters: CasinoDailyPerformanceFilters,
): CasinoDailyPoint[] => {
  const envelope = body as { data?: unknown } | null | undefined;
  const payload = envelope?.data ?? body;
  const rawList = extractDailyRows<RawDailyRow>(payload);

  const byDate = new Map<string, CasinoDailyPoint>();
  for (const row of rawList) {
    const point = mapRow(row);
    if (point) byDate.set(point.date, point);
  }

  // Dense fill: emit a zero-row for every day in the requested range so the
  // chart shows continuous bars rather than gaps when the backend omits
  // empty days. If we can't build a dense range (e.g. invalid filter dates),
  // fall back to the date-sorted real points.
  const denseDates = buildKpiDateRange(filters.start_date, filters.end_date);
  if (denseDates.length > 0) {
    return denseDates.map(
      (date) =>
        byDate.get(date) ?? {
          date,
          turnover: 0,
          ggr: 0,
          rounds: 0,
        },
    );
  }

  return Array.from(byDate.values()).sort((a, b) =>
    a.date.localeCompare(b.date),
  );
};

type RawFilter = {
  key?: unknown;
  options?: unknown;
  cascading_options?: unknown;
};

const toOption = (raw: unknown): CasinoDailyFilterOption | null => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // BE returns the display name verbatim; we use it as both the value and
  // label so a single string round-trips cleanly through the querystring.
  return { value: trimmed, label: trimmed };
};

const looksLikeFilterEntry = (entry: unknown): entry is RawFilter =>
  typeof entry === "object" &&
  entry !== null &&
  typeof (entry as { key?: unknown }).key === "string";

/**
 * Walks the response envelope (up to a few levels deep) hunting for an
 * array whose entries look like filter descriptors (`{ key, options }`).
 * The BE has shipped this catalog at multiple paths over time
 * (`response.filters`, `response.data.filters`, etc) so a flexible
 * search keeps the FE robust to envelope churn rather than silently
 * degrading the dropdowns to "All" when the path moves.
 */
const findFilterArray = (
  node: unknown,
  depth = 0,
): RawFilter[] | null => {
  if (depth > 4 || node === null || node === undefined) return null;

  if (Array.isArray(node)) {
    if (node.some(looksLikeFilterEntry)) {
      return node.filter(looksLikeFilterEntry);
    }
    return null;
  }

  if (typeof node !== "object") return null;
  const record = node as Record<string, unknown>;

  if (Array.isArray(record.filters)) {
    const found = findFilterArray(record.filters, depth + 1);
    if (found && found.length > 0) return found;
  }
  for (const value of Object.values(record)) {
    const found = findFilterArray(value, depth + 1);
    if (found && found.length > 0) return found;
  }
  return null;
};

/**
 * Parses the BE-supplied `cascading_options` map for the `game` filter
 * into a provider-keyed record of pre-shaped option lists. Each value
 * already has the "All Games" catch-all row prepended so the component
 * can render the returned array as-is. Providers that ship with an
 * empty games array still land in the map with just `[ALL_GAMES_OPTION]`
 * so a lookup for that provider is never `undefined`.
 *
 * Defensive: unexpected shapes (null, array, primitive) collapse to
 * an empty map, and non-string entries inside each provider bucket are
 * dropped rather than crashing the parser.
 */
const parseCascadingGameOptions = (
  raw: unknown,
): Record<string, CasinoDailyFilterOption[]> => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};

  const result: Record<string, CasinoDailyFilterOption[]> = {};
  for (const [rawProviderKey, rawGames] of Object.entries(
    raw as Record<string, unknown>,
  )) {
    const providerKey = rawProviderKey.trim();
    if (!providerKey) continue;
    const gameOpts = Array.isArray(rawGames)
      ? rawGames
          .map(toOption)
          .filter((o): o is CasinoDailyFilterOption => o !== null)
          // Drop the BE's "all" sentinel if it slipped into a bucket —
          // we prepend our own localised catch-all so the value is
          // stable ("all") and the label reads consistently.
          .filter((o) => o.value.toLowerCase() !== ALL_VALUE)
      : [];
    result[providerKey] = [ALL_GAMES_OPTION, ...gameOpts];
  }
  return result;
};

/**
 * Maps the BE-supplied `filters` array into FE-shaped dropdown options.
 * `provider` still ships as a plain `options: string[]`; `game` now
 * ships as `cascading_options: { <provider>: string[] }` so the FE can
 * resolve the game list synchronously on provider change instead of
 * waiting for a refetch to narrow it server-side.
 *
 * Always prepends the "all" catch-all to the provider list so admins
 * can clear the filter without typing. Defensive — falls back to
 * `{ providers: [all], gamesByProvider: {} }` when the BE omits or
 * malforms the array so the dropdowns never crash even on a degraded
 * response.
 */
export const parseCasinoDailyFilterOptions = (
  body: unknown,
): CasinoDailyFilterOptions => {
  const rawFilters = findFilterArray(body);
  if (!rawFilters || rawFilters.length === 0) return buildEmptyFilterOptions();

  let providers: CasinoDailyFilterOption[] = [ALL_PROVIDERS_OPTION];
  let gamesByProvider: Record<string, CasinoDailyFilterOption[]> = {};

  for (const filter of rawFilters) {
    if (!filter || typeof filter !== "object") continue;
    if (filter.key === "provider") {
      const opts = Array.isArray(filter.options)
        ? filter.options
            .map(toOption)
            .filter((o): o is CasinoDailyFilterOption => o !== null)
        : [];
      // Drop the BE's "all" sentinel — we prepend our own localised row.
      const realOpts = opts.filter((o) => o.value.toLowerCase() !== ALL_VALUE);
      providers = [ALL_PROVIDERS_OPTION, ...realOpts];
    } else if (filter.key === "game") {
      gamesByProvider = parseCascadingGameOptions(filter.cascading_options);
    }
  }

  return { providers, gamesByProvider };
};

const buildParams = (filters: CasinoDailyPerformanceFilters) => {
  const params: Record<string, string> = {
    start_date: filters.start_date,
    end_date: filters.end_date,
  };
  // Only forward the dropdown filters when the admin has narrowed them
  // beyond "All providers" / "All games". The BE expects the `[eq]`
  // suffix for equality filters, so we ship `provider[eq]=<name>` and
  // `game[eq]=<name>` rather than the bare keys.
  const providerValue = filters["provider[eq]"];
  if (providerValue && providerValue !== ALL_VALUE) {
    params["provider[eq]"] = providerValue;
  }
  const gameValue = filters["game[eq]"];
  if (gameValue && gameValue !== ALL_VALUE) {
    params["game[eq]"] = gameValue;
  }
  return {
    ...params,
    ...buildCasinoKpiMoneyTypeParam(filters.money_type),
  };
};

export const fetchCasinoDailyPerformance = async (
  filters: CasinoDailyPerformanceFilters,
): Promise<CasinoDailyResult> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.CASINO_DAILY_PERFORMANCE, {
    params: buildParams(filters),
  });
  return {
    points: parseCasinoDailyResponse(response?.data, filters),
    filterOptions: parseCasinoDailyFilterOptions(response?.data),
  };
};

export const useGetCasinoDailyPerformance = (
  filters: CasinoDailyPerformanceFilters,
) => {
  const { start_date, end_date } = filters;
  const provider = filters["provider[eq]"];
  const game = filters["game[eq]"];
  const moneyType = filters.money_type;

  return useQuery<CasinoDailyResult, Error>({
    queryKey: [
      "CASINO_DAILY_PERFORMANCE",
      start_date,
      end_date,
      provider ?? ALL_VALUE,
      game ?? ALL_VALUE,
      moneyType?.join(",") ?? "",
    ],
    queryFn: () => fetchCasinoDailyPerformance(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
